import { useState } from "react";
import { Link } from "react-router-dom";
import { useT, useLocalePath } from "@/i18n/LocaleContext";
import CinematicHero from "@/components/CinematicHero";
import GlobalFootprint, { CONTACT_LOCATIONS } from "@/components/GlobalFootprint";

const CATEGORY_KEYS = [
  "institutional_investor", "investment_fund", "regulator",
  "service_provider", "business_partner", "general_enquiry",
];

const COUNTRIES = [
  "Andorra", "Austria", "Belgium", "Bulgaria", "Canada", "Croatia", "Cyprus",
  "Czech Republic", "Denmark", "Estonia", "Finland", "France", "Germany", "Greece",
  "Hungary", "Iceland", "Ireland", "Italy", "Japan", "Latvia", "Liechtenstein",
  "Lithuania", "Luxembourg", "Malta", "Monaco", "Netherlands", "Norway", "Poland",
  "Portugal", "Romania", "Singapore", "Slovakia", "Slovenia", "Spain", "Sweden",
  "Switzerland", "United Kingdom", "United States",
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyState = {
  category: "", first_name: "", last_name: "", organisation: "",
  email: "", country: "", subject: "", message: "", consent: false, honeypot: "",
};

const inputCls = "w-full bg-transparent border-b border-rule px-0 py-3 font-sans text-[15px] text-navy placeholder:text-navy/40 focus:outline-none focus:border-navy transition-colors duration-500";

const Field = ({ label, required, htmlFor, children, error }) => (
  <div className="flex flex-col gap-2">
    <label htmlFor={htmlFor} className="font-sans text-[10.5px] font-medium uppercase text-navy/70" style={{ letterSpacing: "0.22em" }}>
      {label}{required && <span className="text-gold ml-1" aria-hidden>*</span>}
    </label>
    {children}
    {error && <p className="font-sans text-[12px] text-red-700/80" data-testid={`error-${htmlFor}`}>{error}</p>}
  </div>
);

export const Contact = () => {
  const { t } = useT();
  const localePath = useLocalePath();
  const [form, setForm] = useState(emptyState);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const setField = (k, v) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    setErrors((prev) => ({ ...prev, [k]: undefined }));
  };

  const E = t("contact.form.errors");

  const validate = () => {
    const e = {};
    if (!form.category) e.category = E.category;
    if (!form.first_name.trim()) e.first_name = E.required;
    if (!form.last_name.trim()) e.last_name = E.required;
    if (!form.organisation.trim()) e.organisation = E.required;
    if (!form.email.trim()) e.email = E.required;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = E.email;
    if (!form.subject.trim()) e.subject = E.required;
    if (!form.message.trim() || form.message.trim().length < 20) e.message = E.message_min;
    if (form.message.length > 5000) e.message = E.message_max;
    if (!form.consent) e.consent = E.consent;
    return e;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    setStatus("submitting");
    setErrorMsg("");
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (res.status === 200) { setStatus("success"); return; }
      const body = await res.json().catch(() => ({}));
      if (res.status === 429) setErrorMsg(E.rate_limit);
      else setErrorMsg(body.detail || E.generic);
      setStatus("error");
    } catch (_err) {
      setErrorMsg(E.network);
      setStatus("error");
    }
  };

  const reset = () => { setForm(emptyState); setErrors({}); setStatus("idle"); setErrorMsg(""); };

  const activeCategoryKey = form.category;
  const helperKey = activeCategoryKey === "regulator" ? "regulator" : null;
  const helperText = helperKey ? t(`contact.form.helper.${helperKey}`) : "";

  return (
    <>
      <CinematicHero
        imageSrc="/images/contact-hero.jpg"
        imageAlt={t("hero_cinematic.contact.alt")}
        eyebrow={t("hero_cinematic.contact.eyebrow")}
        title={t("hero_cinematic.contact.title")}
        subtitle={t("hero_cinematic.contact.subtitle")}
        testIdPrefix="contact-hero"
      />

      <section id="content" data-testid="contact-page" className="bg-cream">
        <div className="mx-auto max-w-[1360px] px-6 md:px-10 lg:px-14 pt-20 md:pt-28 pb-24 md:pb-40">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          {/* Registered office */}
          <div className="md:col-span-4 reveal">
            <div className="border-t border-rule pt-8">
              <div className="font-sans text-[10.5px] font-medium uppercase text-navy/55 mb-6" style={{ letterSpacing: "0.24em" }}>
                {t("contact.office_heading")}
              </div>
              <address className="not-italic font-serif font-normal text-navy text-[19px] md:text-[21px] leading-[1.7] space-y-1" data-testid="registered-office-block">
                <div>Quantum Capital Management S.A.</div>
                <div>{t("contact.office_address_line1")}</div>
                <div>{t("contact.office_address_line2")}</div>
              </address>
              <dl className="mt-8 space-y-4">
                <div>
                  <dt className="font-sans text-[10.5px] font-medium uppercase text-mute" style={{ letterSpacing: "0.2em" }}>{t("contact.labels.rcs")}</dt>
                  <dd className="font-sans text-[14.5px] font-normal text-navy mt-1">{t("contact.labels.rcs_value")}</dd>
                </div>
                <div>
                  <dt className="font-sans text-[10.5px] font-medium uppercase text-mute" style={{ letterSpacing: "0.2em" }}>{t("contact.labels.lei")}</dt>
                  <dd className="font-sans text-[14.5px] font-normal text-navy mt-1 break-all">{t("contact.labels.lei_value")}</dd>
                </div>
                <div>
                  <dt className="font-sans text-[10.5px] font-medium uppercase text-mute" style={{ letterSpacing: "0.2em" }}>{t("contact.labels.email")}</dt>
                  <dd className="mt-1">
                    <a href="mailto:office@qcmlux.com" data-testid="contact-email-link" className="font-sans text-[14.5px] font-normal text-navy hover:text-gold transition-colors duration-500">office@qcmlux.com</a>
                  </dd>
                </div>
                <div>
                  <dt className="font-sans text-[10.5px] font-medium uppercase text-mute" style={{ letterSpacing: "0.2em" }}>{t("contact.labels.telephone")}</dt>
                  <dd className="mt-1">
                    <a href="tel:+35220420122" data-testid="contact-telephone-link" className="font-sans text-[14.5px] font-normal text-navy hover:text-gold transition-colors duration-500">+352 20 42 01 22</a>
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Form */}
          <div className="md:col-span-7 md:col-start-6 reveal">
            {status === "success" ? (
              <div data-testid="contact-success" className="border-t border-rule pt-10 pb-4">
                <div className="rule-gold w-16 mb-8" aria-hidden />
                <h2 className="font-serif font-normal text-navy text-[26px] md:text-[32px] leading-tight">{t("contact.success.title")}</h2>
                <p className="mt-5 font-serif italic font-normal text-navy/80 text-[18px] md:text-[20px] leading-relaxed max-w-lg">{t("contact.success.body")}</p>
                <button type="button" data-testid="contact-reset" onClick={reset} className="mt-10 font-sans text-[11px] font-medium uppercase text-navy/70 hover:text-navy border-b border-navy/40 hover:border-navy pb-1 transition-colors duration-500" style={{ letterSpacing: "0.24em" }}>
                  {t("contact.success.reset")}
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} noValidate data-testid="contact-form" className="border-t border-rule pt-10 flex flex-col gap-8">
                <div aria-hidden style={{ position: "absolute", left: "-10000px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}>
                  <label htmlFor="website">Website</label>
                  <input type="text" id="website" name="website" tabIndex={-1} autoComplete="off" data-testid="honeypot-field" value={form.honeypot} onChange={(e) => setField("honeypot", e.target.value)} />
                </div>

                <Field label={t("contact.form.contacting_as")} required htmlFor="category" error={errors.category}>
                  <select id="category" data-testid="field-category" className={inputCls} value={form.category} onChange={(e) => setField("category", e.target.value)}>
                    <option value="" disabled>{t("contact.form.select_prompt")}</option>
                    {CATEGORY_KEYS.map((k) => (
                      <option key={k} value={k}>{t(`contact.form.categories.${k}`)}</option>
                    ))}
                  </select>
                  {helperText && <p className="font-sans text-[12.5px] italic text-navy/60 mt-1">{helperText}</p>}
                </Field>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <Field label={t("contact.form.first_name")} required htmlFor="first_name" error={errors.first_name}>
                    <input id="first_name" type="text" data-testid="field-first-name" className={inputCls} value={form.first_name} onChange={(e) => setField("first_name", e.target.value)} autoComplete="given-name" />
                  </Field>
                  <Field label={t("contact.form.last_name")} required htmlFor="last_name" error={errors.last_name}>
                    <input id="last_name" type="text" data-testid="field-last-name" className={inputCls} value={form.last_name} onChange={(e) => setField("last_name", e.target.value)} autoComplete="family-name" />
                  </Field>
                </div>

                <Field label={t("contact.form.organisation")} required htmlFor="organisation" error={errors.organisation}>
                  <input id="organisation" type="text" data-testid="field-organisation" className={inputCls} value={form.organisation} onChange={(e) => setField("organisation", e.target.value)} autoComplete="organization" />
                </Field>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <Field label={t("contact.form.email")} required htmlFor="email" error={errors.email}>
                    <input id="email" type="email" data-testid="field-email" className={inputCls} value={form.email} onChange={(e) => setField("email", e.target.value)} autoComplete="email" />
                  </Field>
                  <Field label={t("contact.form.country")} htmlFor="country">
                    <select id="country" data-testid="field-country" className={inputCls} value={form.country} onChange={(e) => setField("country", e.target.value)}>
                      <option value="">—</option>
                      {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </Field>
                </div>

                <Field label={t("contact.form.subject")} required htmlFor="subject" error={errors.subject}>
                  <input id="subject" type="text" data-testid="field-subject" className={inputCls} value={form.subject} onChange={(e) => setField("subject", e.target.value)} />
                </Field>

                <Field label={t("contact.form.message")} required htmlFor="message" error={errors.message}>
                  <textarea id="message" rows={6} data-testid="field-message" className={`${inputCls} resize-y`} value={form.message} onChange={(e) => setField("message", e.target.value)} maxLength={5000} />
                  <span className="font-sans text-[11px] text-navy/45">{form.message.length} / 5000</span>
                </Field>

                <div className="flex flex-col gap-2">
                  <label className="flex items-start gap-3 cursor-pointer select-none">
                    <input type="checkbox" data-testid="field-consent" checked={form.consent} onChange={(e) => setField("consent", e.target.checked)} className="mt-[3px] h-4 w-4 border border-navy/40 accent-navy" />
                    <span className="font-sans text-[13.5px] font-normal text-navy/80 leading-relaxed">
                      {t("contact.form.consent_prefix")}
                      <Link to={localePath("/legal#privacy")} data-testid="consent-privacy-link" className="underline decoration-gold/60 underline-offset-4 text-navy hover:text-navy">
                        {t("contact.form.consent_link")}
                      </Link>
                      {t("contact.form.consent_suffix")}
                    </span>
                  </label>
                  {errors.consent && <p className="font-sans text-[12px] text-red-700/80" data-testid="error-consent">{errors.consent}</p>}
                </div>

                {status === "error" && errorMsg && (
                  <div data-testid="contact-error" className="font-sans text-[13.5px] text-red-700/85 border-t border-red-700/20 pt-4">
                    {errorMsg}
                  </div>
                )}

                <div className="pt-4">
                  <button type="submit" data-testid="contact-submit" disabled={status === "submitting"} className="bg-navy text-cream font-sans text-[11.5px] font-medium uppercase px-10 py-4 hover:bg-navy/90 disabled:opacity-50 transition-colors duration-500" style={{ letterSpacing: "0.24em" }}>
                    {status === "submitting" ? t("contact.form.sending") : t("contact.form.submit")}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* Closing map — where the Company operates */}
      </div>
    </section>

    <GlobalFootprint
      locations={CONTACT_LOCATIONS}
      originId="lu"
      eyebrowKey="contact_map.eyebrow"
      headingKey="contact_map.heading"
      bodyKey="contact_map.subtitle"
      mapAltKey="contact_map.map_alt"
      captionKey="contact_map.caption"
      countryNamespace="contact_map.countries"
      testIdPrefix="contact-footprint"
    />
    <div className="section-bridge-cream-navy" aria-hidden data-testid="contact-bridge-footer" />
    </>
  );
};

export default Contact;
